import React, { useState, useEffect } from "react";
import axios from "axios";
import Loading from "../Modals/Loading";
import moment from "moment";
import { useLocation } from "react-router-dom";
import accountService from "../Services/accountService";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";


const AccountLedger = () => {
  const [ledger, setLedger] = useState([]);
  const [account, setAccount] = useState({});
  const [loading, setLoading] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  useEffect(() => {
    const getLedgerData = async () => {
      try {
        setLoading(true);
        const response = await accountService.fetchBankLedger(params.get("id"));
        setLedger(response.ledger);
        setAccount(response.account);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching ledger:", error);
        setLoading(false);
      }
    };
    getLedgerData();
  }, []);

  const exportToExcel = () => {
    const exportData = ledger.map((entry) => ({
      Date: moment(entry.date).format("YYYY-MM-DD"),
      Note: entry.note,
      Credit: entry.direction === "CREDIT" ? entry.amount : "",
      Debit: entry.direction === "DEBIT" ? entry.amount : "",
      Balance: entry.balance,
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Ledger");
  
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
  
    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
  
    saveAs(fileData, `ledger-${account.name}.xlsx`);
  };
  
  // Totals Calculation
  const totalCredit = ledger
    .filter((entry) => entry.direction === "CREDIT")
    .reduce((sum, entry) => sum + entry.amount, 0);

  const totalDebit = ledger
    .filter((entry) => entry.direction === "DEBIT")
    .reduce((sum, entry) => sum + entry.amount, 0);

  const finalBalance =
    ledger.length > 0
      ? ledger[ledger.length - 1].balance
      : account.opening_balance;

  return (
    <main className="mx-10 my-2 p-6 rounded-3xl border border-gray-200 shadow-lg mb-20 bg-white">
      {loading ? (
        <Loading />
      ) : (
        <>
          {/* <p className="font-bold text-3xl text-secondary">Account Ledger</p> */}
          <div className="flex justify-between items-center mt-4">
            <p className="font-bold text-3xl text-secondary">Account Ledger</p>
            <button
              onClick={exportToExcel}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition"
            >
              Download Excel
            </button>
          </div>


          {/* Account Info */}
          <div className="bg-gray-50 border border-gray-300 px-8 py-4 mt-2 rounded-3xl">
            <div className="text-primary text-2xl font-bold flex flex-row items-center gap-2">
              <h1>{account.name}</h1>
            </div>
            <h1 className="text-sm text-gray-600 font-semibold mt-4">
              Phone No: <span className="text-sky-600">{account.number}</span>
            </h1>
            <div className="text-sm text-gray-600 font-semibold mt-2">
              Opening Balance:{" "}
              <span className="text-lime-500">{account.opening_balance}</span>
            </div>
            <div className="text-sm text-gray-600 font-semibold mt-2">
              Ending Balance:{" "}
              <span className="text-emerald-700">
                {account.current_balance}
              </span>
            </div>
          </div>

          {/* Ledger Table */}
          <div className="table-div mt-4">
            <table>
              <thead className="border-y-2 text-left w-full text-black text-[10px]">
                <tr className="uppercase w-full text-center">
                  <th className="py-4 w-[2%] text-left">S.No</th> 
                  <th className="py-4 w-[2%] text-left">Date</th>
                  <th className="py-4 w-[2%] text-center">Voucher Type</th>
                  <th className="py-4 w-[6%] text-left">Note</th>
                  <th className="w-[2%] py-4">Credit</th>
                  <th className="w-[2%] py-4">Debit</th>
                  <th className="w-[2%] py-4">End Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((entry, index) => (
                  <tr
                    key={index}
                    className="bg-accent border-b border-gray-100 text-center"
                  >
                    <td className="py-4 w-[2%] text-left font-semibold">{index + 1}</td>
                    <td className="py-4 w-[2%] text-left font-semibold">
                      {moment(entry.date).format("ll")}
                    </td>
                    {/* <td className="py-4 w-[2%] text-center text-[10px] font-semibold text-gray-600">
                      {entry.voucher}
                    </td> */}
                    <td className="py-4 w-[2%] text-center text-[10px] font-semibold text-gray-600">
                      {entry.voucher || (entry.note?.split(" ")[0] || "")}
                    </td>
                    <td className="py-4 w-[6%] text-left text-[10px] font-semibold text-gray-600">
                      {entry.note}
                    </td>
                    <td className="py-4 w-[2%]">
                      {entry.direction === "CREDIT" &&
                      typeof entry.amount === "number"
                        ? entry.amount.toFixed(2)
                        : "-"}
                    </td>
                    <td className="py-4 w-[2%]">
                      {entry.direction === "DEBIT" &&
                      typeof entry.amount === "number"
                        ? entry.amount.toFixed(2)
                        : "-"}
                    </td>

                    <td className="py-4 w-[2%] font-bold text-teal-600">
                      {typeof entry.balance === "number"
                        ? entry.balance.toFixed(2)
                        : "-"}
                    </td>
                  </tr>
                ))}
                <tr className="text-[14px] bg-slate-200 font-semibold text-teal-600">
                  <td colSpan={3} className="py-3 pl-4">
                    Totals
                  </td>
                  <td className="py-3 text-center">
                    {typeof totalCredit === "number"
                      ? totalCredit.toFixed(2)
                      : "-"}
                  </td>
                  <td className="py-3 text-center">
                    {typeof totalDebit === "number"
                      ? totalDebit.toFixed(2)
                      : "-"}
                  </td>
                  <td className="py-3 text-center">
                    {typeof finalBalance === "number"
                      ? finalBalance.toFixed(2)
                      : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
};

export default AccountLedger;
